import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Flex, FormControl, FormLabel, Input, Button,
  Text, VStack, InputGroup, InputRightElement, IconButton, Image, Spinner, Center
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { login, getUserInfo, tokenUtils, refreshAccessToken } from '../controller/api';

const MainPage = () => {
  const navigate = useNavigate();

  // 인증이 끊긴 상태로 QR 딥링크에 진입하면 ?next=<원래 주소>를 달고
  // 여기로 돌아온다. 로그인 후 그 주소로 복귀시킨다.
  // 앱 내부의 배송 화면 경로만 허용해 외부 주소로 이동하는 일을 막는다.
  const [searchParams] = useSearchParams();
  const nextParam = searchParams.get('next');
  const nextTarget = nextParam && nextParam.startsWith('/delivery') ? nextParam : '/delivery';

  // 상태 관리
  const [form, setForm] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');

  // 인증 상태 관리 추가
  const [isChecking, setIsChecking] = useState(true);

  const verifyAuth = useCallback(async () => {
    console.log("자동 로그인 확인 중...");
    try {
      // 1. 세션 또는 로컬 스토리지에서 액세스 토큰 확인
      if (tokenUtils.hasAccessToken()) {
        console.log("액세스 토큰 확인됨:", tokenUtils.hasAccessToken());

        try {
          // 사용자 정보 가져오기 시도 (토큰 유효성 검증)
          const userInfo = await getUserInfo(localStorage.getItem('accessToken'));
          if (userInfo?.payload?.nickname) {
            console.log("사용자 정보 확인됨:", userInfo.payload.nickname);
            localStorage.setItem('driverName', userInfo.payload.nickname);
            navigate(nextTarget);
            return;
          }
        } catch (error) {
          console.log("액세스 토큰 만료됨, 리프레시 시도");
          // 액세스 토큰이 만료되었을 수 있으므로 리프레시 토큰 확인으로 진행
        }
      }

      // 2. 리프레시 토큰 확인
      if (tokenUtils.hasRefreshToken()) {
        console.log("리프레시 토큰 확인됨");
        try {
          // 리프레시 토큰으로 새 액세스 토큰 요청
          const refreshToken = localStorage.getItem('refreshToken');
          const response = await refreshAccessToken(refreshToken);

          console.log('새로운 액세스 토큰 발급됨:', response);

          if (response?.access_token) {
            // 새 토큰 저장
            tokenUtils.setTokens(response.access_token, response.refresh_token);

            // 사용자 정보 조회 실패로 방금 발급받은 세션을 버리면 안 된다.
            // 이 호출이 바깥 catch로 넘어가면 clearTokens가 실행되어 유효한
            // 토큰까지 지워지고 기사님이 다시 로그인해야 한다.
            try {
              const userInfo = await getUserInfo(response.access_token);
              if (userInfo?.payload?.nickname) {
                localStorage.setItem('driverName', userInfo.payload.nickname);
              }
            } catch (userInfoError) {
              console.error('사용자 정보 가져오기 실패 (세션은 유지):', userInfoError);
            }

            navigate(nextTarget);
          } else {
            // 토큰 응답이 올바르지 않음
            console.error('토큰 응답 형식이 올바르지 않습니다:', response);
            tokenUtils.clearTokens();
            setMessage('로그인이 만료되었습니다. 다시 로그인해주세요.');
          }
        } catch (error) {
          // 리프레시 토큰 만료 또는 API 오류
          console.error('토큰 갱신 실패:', error);
          tokenUtils.clearTokens();
          setMessage('로그인 세션이 만료되었습니다. 다시 로그인해주세요.');
        }
      }
    } finally {
      setIsChecking(false);
    }
  }, [navigate, nextTarget, setIsChecking, setMessage]);

  useEffect(() => {
    verifyAuth();
  }, [verifyAuth]);

  // 이벤트 핸들러
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setMessage('');
  };

  const togglePassword = () => setShowPassword(prev => !prev);

  // 사용자 정보 가져오기
  const fetchUserInfo = async (accessToken) => {
    try {
      const userInfo = await getUserInfo(accessToken);
      if (userInfo?.payload?.nickname) {
        localStorage.setItem('driverName', userInfo.payload.nickname);
      }
      return true;
    } catch (error) {
      console.error('사용자 정보 가져오기 실패:', error);
      return false;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const { username, password } = form;

    // 입력 검증
    if (!username || !password) {
      setMessage('아이디와 비밀번호를 모두 입력해주세요.');
      return;
    }

    try {
      setIsLoading(true);
      setMessage('');

      const response = await login(username, password);

      if (response?.access_token) {
        // 토큰 저장 - API 유틸리티 사용
        tokenUtils.setTokens(response.access_token, response.refresh_token);

        // 사용자 정보 가져오기
        await fetchUserInfo(response.access_token);

        // 배송 페이지로 이동
        navigate(nextTarget);
      } else {
        setMessage('로그인에 실패했습니다. 다시 시도해주세요.');
      }
    } catch (error) {
      console.error('로그인 오류:', error);

      // 오류 응답 처리
      const status = error.response?.status;
      if (status === 400 || status === 401) {
        setMessage('아이디 또는 비밀번호가 올바르지 않습니다.');
      } else if (status === 429) {
        setMessage('너무 많은 요청을 보냈습니다. 잠시 후 다시 시도해주세요.');
      } else {
        setMessage('서버 연결 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      }
    } finally {
      setIsLoading(false);
      setForm(prev => ({ ...prev, password: '' }));

    }
  };

  // 인증 확인 중일 때 로딩 화면 표시
  if (isChecking) {
    return (
      <Center height="100vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="orange.500" thickness="4px" />
          <Text>자동 로그인 중...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Box bg="white" minH="100vh">
      <Flex direction="column" align="center" pt={12} px={6}>
        {/* 로고 영역 */}
        <Box mb={8} textAlign="center">
          <Image
            src="/test_app_logo.png"
            alt="쿡배송 로고"
            width="120px"
            mb={4}
          />
          <Text color="gray.600" mt={2}>배송기사용 앱</Text>
        </Box>

        {/* 로그인 폼 */}
        <Box
          as="form"
          p={8}
          width="100%"
          maxW="400px"
          borderWidth={1}
          borderRadius="lg"
          boxShadow="md"
          bg="white"
          onSubmit={handleLogin}
        >
          <VStack spacing={4}>
            {/* 메시지 영역 */}
            {message && (
              <Box py={2} width="100%" textAlign="center">
                <Text color="gray.700">{message}</Text>
              </Box>
            )}

            {/* 아이디 입력 */}
            <FormControl>
              <FormLabel>아이디</FormLabel>
              <Input
                name="username"
                value={form.username}
                onChange={handleChange}
                placeholder="아이디를 입력하세요"
                autoComplete="username"
              />
            </FormControl>

            {/* 비밀번호 입력 */}
            <FormControl>
              <FormLabel>비밀번호</FormLabel>
              <InputGroup>
                <Input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={handleChange}
                  placeholder="비밀번호를 입력하세요"
                  autoComplete="current-password"
                  sx={{
                    WebkitTextSecurity: showPassword ? 'none' : 'disc',
                    '&[type="password"]': {
                      fontFamily: 'password',
                      WebkitTextSecurity: 'disc',
                    }
                  }}
                />
                <InputRightElement>
                  <IconButton
                    aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                    icon={showPassword ? <FaEyeSlash /> : <FaEye />}
                    onClick={togglePassword}
                    variant="ghost"
                    size="sm"
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            {/* 로그인 버튼 */}
            <Button
              mt={6}
              colorScheme="orange"
              width="100%"
              type="submit"
              isLoading={isLoading}
              loadingText="로그인 중..."
            >
              로그인
            </Button>
          </VStack>
        </Box>
      </Flex>
    </Box>
  );
};

export default MainPage;